/**
 * Owns the one live `MediaStream` behind whatever in this app currently needs a hot
 * microphone. `AssistantController` (Y alone, an open turn, or both at once) and
 * `NodeLabelController`'s button-held label capture both subscribe to the *same* instance —
 * wired once, in `main.ts` — rather than each opening the device on its own. Capture streams
 * in chunks rather than arriving as one blob when it ends — plan/ai-in-room.md, "Audio is
 * chunked" — so a room relay could fan the same chunks out live if that stretch goal ever
 * lands.
 *
 * **One hot device, N independent recorders — not N listeners on one shared recorder.**
 * `start`/`stop` are a subscribe/unsubscribe pair keyed by the exact function reference a
 * caller passes: the underlying `getUserMedia` stream opens for the first subscriber and
 * closes once the last one unsubscribes, but every subscriber gets its **own**
 * `MediaRecorder` against that shared stream, not a shared fan-out of one recorder's chunks.
 *
 * <p>This replaces an earlier design — one shared `MediaRecorder`, every subscriber's
 * callback added to a fan-out set, with a retained "init segment" (the recorder's own first
 * `ondataavailable` chunk) handed synchronously to any subscriber joining mid-stream so its
 * assembled audio would start with a decodable WebM header. That design shipped, and it
 * still had a production bug: a live, credentialed run against Gemini's
 * `TranscribeLabelAsync` endpoint (headless-Chromium `MediaRecorder`, not a fake) showed the
 * *first* `ondataavailable` event is not reliably the complete WebM initialization segment —
 * one real capture flushed a genuine EBML+Segment+Tracks header across its first **two**
 * chunks, with the very first landing as a single byte (`0x1a`, the leading byte of the EBML
 * magic number alone). Retaining only "the first chunk" as the header handed a late
 * subscriber an incomplete, undecodable stream — HTTP 400 `INVALID_ARGUMENT` from Gemini most
 * of the time, exactly the intermittent production symptom this class exists to fix. There is
 * no reliable client-side way to know how many early chunks a real `MediaRecorder`
 * implementation needs before the header is actually complete; it is an implementation detail
 * of the browser's own muxer, observed to vary chunk-to-chunk even for the identical audio
 * content re-recorded moments later.</p>
 *
 * <p>The fix proven against that same real endpoint: give every subscriber its own
 * `MediaRecorder`, so every subscriber's own stream starts with its own recorder's own first
 * chunk — genuinely, unconditionally the true header, the same guarantee a lone subscriber
 * with no sharing at all already had. Multiple independent `MediaRecorder` instances against
 * one shared `MediaStream` is a real, working browser capability (confirmed the same way —
 * two concurrent recorders against one synthetic stream, both producing complete, separately
 * decodable, separately transcribable WebM clips). This also deletes an entire class of
 * "is this chunk the first one" bookkeeping (the old `headerChunk`/`isFirstChunk`/`sendQueue`
 * machinery) rather than patching it — there is no header to retain or splice any more,
 * because nothing downstream of `getUserMedia` is shared between subscribers except the
 * hardware itself.</p>
 */

/** How often `MediaRecorder` hands back a chunk. Small enough to feel "live" if a future
 *  relay consumes these; large enough that per-chunk overhead does not dominate. */
export const CHUNK_INTERVAL_MS = 250;

/**
 * Tried in preference order. `audio/webm;codecs=opus` is what Chromium — and therefore
 * Quest Browser — actually produces; the rest are fallbacks for a browser this project is
 * not primarily tested on. An empty result lets `MediaRecorder` pick its own default rather
 * than throwing, which happens if literally none of these are supported.
 */
const MIME_TYPE_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4',
];

export class MicrophoneCapture {
  private stream: MediaStream | null = null;

  /** Set only while a `getUserMedia` call is genuinely in flight — lets a second subscriber
   *  racing the first await the same open rather than triggering a second device request.
   *  Cleared once that call settles, success or failure, so a rejection does not wedge every
   *  later `start()` behind a permanently-failed promise (`ensureStream`'s own doc). */
  private streamOpenPromise: Promise<MediaStream> | null = null;

  /** One independent `MediaRecorder` per currently-subscribed listener — see this class's
   *  own module doc for why a subscriber owns its own recorder rather than sharing one. */
  private readonly recorders = new Map<(bytes: Uint8Array) => void, MediaRecorder>();

  /**
   * Resolves the permission prompt and immediately releases the device — this exists only
   * to make the browser ask, from the splash screen's own explicit gesture, before an XR
   * session (which cannot render a permission prompt at all) is ever entered. `start()`
   * below re-opens the device for real capture; a browser that has already granted
   * permission resolves that instantly, with no second prompt.
   */
  static async requestPermission(): Promise<boolean> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      for (const track of stream.getTracks()) {
        track.stop();
      }

      return true;
    } catch {
      return false;
    }
  }

  /** The best supported capture format, or `''` to let MediaRecorder pick its own default. */
  static pickMimeType(): string {
    if (typeof MediaRecorder === 'undefined') {
      return '';
    }

    return MIME_TYPE_CANDIDATES.find((candidate) => MediaRecorder.isTypeSupported(candidate)) ?? '';
  }

  /** True while at least one subscriber is currently capturing — the hardware is hot for
   *  *some* reason, not necessarily this particular caller's own subscription (see each
   *  consumer's own "am I subscribed" tracking, e.g. `AssistantController.capturingHardware`). */
  get isCapturing(): boolean {
    return this.recorders.size > 0;
  }

  /**
   * Subscribes `onChunk` to its own, independent `MediaRecorder` against the shared device —
   * opening that device first if this is the first subscriber overall. A caller must hold
   * onto exactly the reference it passes here and hand that same reference back to `stop()`
   * later; `MicrophoneCapture` tells one subscriber's unsubscribe apart from another's by
   * that reference alone.
   *
   * A no-op if `onChunk` is already subscribed (the same reference, re-entrant) — matching
   * the old shared-recorder design's own tolerance for a duplicate call.
   */
  async start(mimeType: string, onChunk: (bytes: Uint8Array) => void): Promise<void> {
    if (this.recorders.has(onChunk)) {
      return;
    }

    const stream = await this.ensureStream();

    // Compared against the field, not just "is there a stream at all": while this call was
    // awaiting `ensureStream` above, every other subscriber could have unsubscribed (closing
    // the device entirely) and a fresh one since re-opened it as a genuinely different
    // `MediaStream` object. Either way, `stream` (the local value this call resolved) is not
    // the one this class currently considers live, and standing up a recorder against it —
    // closed already, or simply not the shared instance any more — is not what a caller
    // joining "the" device asked for. `stop()` cannot have found this reference to clean up
    // in the meantime either way, since it was never added to `recorders` until below.
    if (this.stream !== stream) {
      return;
    }

    const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);

    // Chains chunk delivery so a slow Blob→bytes conversion cannot deliver a later chunk to
    // the server ahead of an earlier one — `ondataavailable` fires close enough together, at
    // CHUNK_INTERVAL_MS, that firing each conversion independently is a real risk, not a
    // theoretical one. Scoped per recorder now, not shared across subscribers, since each
    // subscriber's chunk order only ever has to stay consistent with itself.
    let sendQueue: Promise<void> = Promise.resolve();

    recorder.ondataavailable = (event) => {
      if (event.data.size === 0) {
        return;
      }

      sendQueue = sendQueue
        .then(() => event.data.arrayBuffer())
        .then((buffer) => onChunk(new Uint8Array(buffer)))
        .catch((error: unknown) =>
          console.warn('Assistant: failed to process a captured audio chunk.', error),
        );
    };

    recorder.start(CHUNK_INTERVAL_MS);
    this.recorders.set(onChunk, recorder);
  }

  /**
   * Unsubscribes `onChunk` — the same reference passed to `start()`. A no-op if that
   * listener is not currently subscribed (never started, or already stopped): safe to call
   * defensively.
   *
   * That subscriber's own `MediaRecorder` stops immediately; the underlying device itself
   * only closes once this was the *last* subscriber — while anyone else is still listening,
   * this leaves the shared hardware (and their own independent recorder) running untouched.
   */
  stop(onChunk: (bytes: Uint8Array) => void): void {
    const recorder = this.recorders.get(onChunk);

    if (!recorder) {
      return;
    }

    this.recorders.delete(onChunk);
    recorder.stop();

    if (this.recorders.size > 0) {
      return;
    }

    for (const track of this.stream?.getTracks() ?? []) {
      track.stop();
    }

    this.stream = null;
  }

  /**
   * Resolves the shared `MediaStream`, opening the device only if nothing has yet (or the
   * previous open failed and nothing is currently in flight). Concurrent callers racing the
   * very first `start()` all await the identical in-flight `getUserMedia` call rather than
   * each triggering their own — `streamOpenPromise` is cleared in `finally` regardless of
   * outcome, so a rejection here does not wedge every later subscriber behind a permanently
   * failed promise; the next call simply tries again.
   */
  private async ensureStream(): Promise<MediaStream> {
    if (this.stream) {
      return this.stream;
    }

    if (this.streamOpenPromise) {
      return this.streamOpenPromise;
    }

    const promise = navigator.mediaDevices.getUserMedia({ audio: true });
    this.streamOpenPromise = promise;

    try {
      const stream = await promise;
      this.stream = stream;
      return stream;
    } finally {
      this.streamOpenPromise = null;
    }
  }
}
